import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Phone, Plus, User } from "lucide-react";
import { fetchContacts, createContact } from "@/lib/api";
import { formatCallTime, initials } from "@/lib/format";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const Contacts = () => {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data: contacts = [], isLoading } = useQuery({
    queryKey: ["contacts"], queryFn: fetchContacts, refetchInterval: 30000, refetchOnWindowFocus: true,
  });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ number: "", name: "", company: "", note: "" });

  const addMut = useMutation({
    mutationFn: () => createContact(form),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["contacts"] }); toast.success("Contact ajouté"); setOpen(false); setForm({ number: "", name: "", company: "", note: "" }); },
    onError: () => toast.error("Impossible d'ajouter le contact"),
  });

  return (
    <div data-testid="contacts-page" className="space-y-6">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-heading font-bold text-slate-900 text-2xl sm:text-3xl tracking-tight">Contacts</h1>
          <p className="hidden md:block text-sm text-slate-500 mt-1">Personnes ayant échangé avec votre standard.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button data-testid="add-contact-btn" className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 text-white text-sm font-medium px-3 sm:px-4 py-2 hover:bg-emerald-700 transition-colors shrink-0">
              <Plus className="h-4 w-4" /> <span className="hidden sm:inline">Ajouter un contact</span><span className="sm:hidden">Ajouter</span>
            </button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[400px]">
            <DialogHeader>
              <DialogTitle className="font-heading">Nouveau contact</DialogTitle>
              <DialogDescription className="sr-only">Créer un contact</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-sm">Numéro</Label><Input data-testid="contact-number-input" value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} placeholder="06 51 59 75 06" className="mt-1" /></div>
              <div><Label className="text-sm">Nom</Label><Input data-testid="contact-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex : Garage Dupont" className="mt-1" /></div>
              <div><Label className="text-sm">Société</Label><Input data-testid="contact-company-input" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="mt-1" /></div>
              <div><Label className="text-sm">Note</Label><Textarea data-testid="contact-note-input" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2} placeholder="Client — Clio 4, pneus 205/55 R16" className="mt-1" /></div>
            </div>
            <DialogFooter>
              <button data-testid="save-contact-btn" disabled={!form.number.trim() || addMut.isPending} onClick={() => addMut.mutate()} className="rounded-lg px-4 py-2 text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">Enregistrer</button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </header>

      {isLoading ? (
        <div className="rounded-xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-400">Chargement…</div>
      ) : contacts.length ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="contacts-grid">
          {contacts.map((c) => (
            <button
              key={c.number_key}
              data-testid={`contact-${c.number_key}`}
              onClick={() => navigate(`/contacts/${c.number_key}`)}
              className="text-left rounded-xl border border-slate-200 bg-white shadow-sm active:bg-slate-50 hover:shadow-md transition-all p-4 flex items-center gap-3"
            >
              <span className="h-11 w-11 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-sm font-semibold text-slate-500 shrink-0">
                {c.has_name ? initials(c.name) : <User className="h-5 w-5 text-slate-400" />}
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-medium text-slate-900 text-sm truncate">{c.name}</p>
                {c.company && <p className="text-xs text-slate-400 truncate">{c.company}</p>}
                <p className="text-xs text-slate-400 mt-0.5 flex items-center gap-1">
                  <Phone className="h-3 w-3" /> {c.call_count} appel{c.call_count > 1 ? "s" : ""}{c.last_call ? ` · ${formatCallTime(c.last_call)}` : ""}
                </p>
              </div>
            </button>
          ))}
        </div>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-400">Aucun contact pour le moment.</div>
      )}
    </div>
  );
};

export default Contacts;
